/**
 * CampaignListItem
 *
 * One campaign card: centered title, a rounded card body (with an optional faint
 * background image when `campaign.image` is set), subtitle, an optional
 * "CONDITIONS" block, a "Learn more" link (only when `campaign.policy` is set),
 * the date range, and the ranking button.
 *
 * Mirrors Flutter `lib/presentation/screens/campaigns/widgets/campaign_list_item.dart`:
 *  - title (:28-32), card background image (:34-55, only when `image != null`),
 *    subtitle (:72-79), conditions block (:80-124, only when `conditions != null`),
 *    "Learn more" (:127-139), date range (:141-151), ranking button (:153-194).
 *
 * Web deviations (permitted UI tokens, behaviour preserved):
 *  - "Learn more" routes to the Rewards Policy page via
 *    `Navigator.navigateToRewardsPolicy()` — matching Flutter's web branch in
 *    `lib/services/navigation_service.dart:1790-1793` (on `kIsWeb` the campaign
 *    policy opens the Rewards Policy page, not a webview).
 *  - Date range uses `useDateFormatters().formatDate` (the web analog of
 *    Flutter's `getCampaignDate`) — see the Deviations note in task_8_plan.md.
 *  - Ranking button: uses the shared `ExpauseButton` component with the
 *    `primary` variant while active and `secondary` once ended, rather than the
 *    bordered surface in Flutter (`AppColors.exclusive` / `AppColors.activeRed`).
 *    This is the user-requested Web custom-component treatment that supersedes
 *    the Flutter-parity border-color styling.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseButton, ButtonVariant, ButtonSize } from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import type { CampaignEntity } from '@/domain/entities/campaign/campaignEntity';
import { CampaignConditionsList } from './CampaignConditionsList';

interface CampaignListItemProps {
  campaign: CampaignEntity;
  onOpenRanking: (campaign: CampaignEntity) => void;
}

export const CampaignListItem = ({ campaign, onOpenRanking }: CampaignListItemProps) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const { formatDate } = useDateFormatters();

  // Flutter gates the "CONDITIONS" header on `campaign.conditions != null`
  // (campaign_list_item.dart:81) — an explicit empty array still renders the
  // header (with zero rows), so the gate is non-null, not non-empty.
  const hasConditions = campaign.conditions != null;
  // Default-bucket image — route through the shared helper so the `o/` download
  // segment is normalised to exactly one regardless of env prefix / stored-path
  // shape (mirrors how user media resolves via `getMediaThumbnailUrl`).
  const backgroundImageUrl =
    campaign.image != null ? MediaUtils.joinDefaultBucketUrl(campaign.image) : undefined;

  const rankingLabel = campaign.active ? l10n.campaigns.liveStats : l10n.campaigns.results;
  const dateRange = `${formatDate(campaign.startDate)} - ${formatDate(campaign.endDate)}`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: `${responsive.spacing.lg}px 0`,
      }}
    >
      <div
        style={{
          marginBottom: responsive.spacing.sm,
          color: theme.text.secondary,
          fontSize: responsive.font['2xl'],
          textAlign: 'center',
        }}
      >
        {campaign.title}
      </div>

      <div
        style={{
          width: '100%',
          borderRadius: responsive.borderRadius.base,
          backgroundColor: theme.modalBackground,
          ...(backgroundImageUrl
            ? {
              backgroundImage: `url(${backgroundImageUrl})`,
              backgroundPosition: 'top center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }
            : {}),
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: `${responsive.spacing.lg}px ${responsive.spacing['3xl']}px`,
          }}
        >
          {campaign.subtitle && (
            <div
              style={{
                marginBottom: responsive.spacing['3xl'],
                color: theme.text.secondary,
                fontSize: responsive.font.base,
                textAlign: 'center',
              }}
            >
              {campaign.subtitle}
            </div>
          )}

          {hasConditions && <CampaignConditionsList conditions={campaign.conditions!} />}

          {campaign.policy != null && (
            <ExpauseButton
              variant={ButtonVariant.text}
              onClick={() => Navigator.navigateToRewardsPolicy()}
              style={{ fontSize: responsive.font.base }}
            >
              {l10n.campaigns.learnMore}
            </ExpauseButton>
          )}

          <div
            style={{
              marginTop: responsive.spacing['3xl'],
              marginBottom: responsive.spacing.xs,
              color: theme.text.secondary,
              fontSize: responsive.font.sm,
              textAlign: 'center',
            }}
          >
            {dateRange}
          </div>

          <ExpauseButton
            {...qaAttr({ id: `campaign-ranking-btn-${campaign.id}` })}
            variant={campaign.active ? ButtonVariant.primary : ButtonVariant.secondary}
            size={ButtonSize.medium}
            onClick={() => onOpenRanking(campaign)}
            style={{
              margin: `0 ${responsive.spacing['3xl']}px`,
            }}
          >
            {rankingLabel}
          </ExpauseButton>
        </div>
      </div>
    </div>
  );
};
